import logging from '../logging';

const log = logging('ready-signal');

/**
 * The OnReady interface can be implemented, but more importantly the
 * hasOnReady function can be used to satisfy the TypeScript compiler when we
 * duck type things that have a ReadySignal property named `onReady`.
 */
export interface OnReady {
    onReady: ReadySignal;
}

/**
 * This function lets you cast objects to the OnReady interface in a TypeScript
 * friendly manner.
 */
export const hasOnReady = (item: any): item is OnReady => {
    if(item && item.onReady && item.onReady instanceof ReadySignal) {
        return true;
    }
    return false;
}

enum Status {
    FRESH = 0,
    READY = 1,
    TRIGGERED = 2,
}

/**
 * This class helps to manage the complexity of deciding when a system of
 * interconnected objects are completely ready.
 *
 * Because this does a lot of checks for each node, performance will probably
 * be poor for large networks. For the current use case of checking that every
 * object in the scene is ready for rendering, this should be fine.
 *
 * Example usage. In this example, since dependencies are a -> b, a -> c, and
 * b -> d, we should always see a's callback after the last signal has been
 * triggered.
 *
 *    const
 *        a = new ReadySignal('a'),
 *        b = new ReadySignal('b'),
 *        c = new ReadySignal('c'),
 *        d = new ReadySignal('d');
 *
 *    a.add(() => console.log('a ready'));
 *    b.add(() => console.log('b ready'));
 *    c.add(() => console.log('c ready'));
 *    d.add(() => console.log('d ready'));
 *
 *    a.depends(b, c);
 *    b.depends(d);
 *
 *    const signals = [a, b, c, d];
 *    signals.sort((_a, _b) => Math.random()-0.5);
 *    for(const s of signals) {
 *        console.log('trigger', s.name);
 *        s.trigger();
 *    }
 */
export default class ReadySignal {

    private status: Status = Status.FRESH;
    private handlers: Set<() => unknown> = new Set();
    private dependencies: Set<ReadySignal> = new Set();

    public name: string;

    /**
     * @param name - This makes debugging so much easier that omitting the name
     * would be tragic.
     */
    public constructor(name: string) {
        this.name = name;
    }

    /**
     * Adds the handler to the set of callbacks. If this has already been
     * triggered, i.e. this is ready and all dependencies have triggered, then
     * the handler is called immediately. Any exceptions thrown by the handler
     * are caught and logged.
     */
    public add(handler: () => unknown) {
        this.handlers.add(handler);
        if(this.status == Status.TRIGGERED) {
            try {
                handler();
            } catch(error) {
                log.error(error);
            }
        }
    }

    /**
     * Returns a promise that resolves when this instance becomes ready. If
     * the instance is already ready, then the promise resolves immediately.
     * @param timeout - how long to wait, in milliseconds, before rejecting the
     * returned promise.
     */
    public wait(timeout: number): Promise<void> {
        if(this.status == Status.TRIGGERED) {
            return Promise.resolve();
        } else {
            return new Promise((resolve, reject) => {
                // Stupid NodeJS has a separate type for timeout results, so we
                // typecast to any to shut TypeScript up.
                const timeoutId: number = setTimeout(() => {
                    this.handlers.delete(handler);
                    reject();
                }, timeout) as any;

                const handler = () => {
                    clearTimeout(timeoutId);
                    this.handlers.delete(handler);
                    resolve();
                };
                this.add(handler);
            });
        }
    }

    /**
     * Optionally adds one or more ready signals on which this signal should
     * depend. If there are any dependencies, then calling trigger() won't
     * cause the callbacks to fire unless all the dependencies are in a
     * non-FRESH state. Once this has been triggered AND all dependencies have
     * been triggered, then this will call the handlers.
     */
    depends(...others: ReadySignal[]) {
        if(this.status != Status.FRESH) {
            log.debug(`ReadySignal.depends() "${this.name}" attempt to add dependency after already triggered`);
        }
        for(const other of others) {
            this.dependencies.add(other);
            other.add(this.resolveDependency);
        }
    }

    /**
     * This may call the handlers if all the dependencies have already been
     * triggered, otherwise the class waits until all dependencies have been
     * triggered to call the handlers.
     *
     * The code using this class should call `trigger()`  when this particular
     * ReadySignal is ready. If the ready signal only depends on its children
     * and has no sensible status of its own, the caller should trigger it
     * after adding the dependencies.
     */
    public trigger = () => {
        switch(this.status) {
            case Status.FRESH:
                log.debug(`ReadySignal ${this.name} triggerd, FRESH -> READY`);
                this.status = Status.READY;
                // note that this case inentionally falls through
            case Status.READY:
                if(this.allDependenciesHaveTriggered()) {
                    log.debug(`ReadySignal ${this.name} triggerd, READY -> TRIGGERED, calling handlers`);
                    this.status = Status.TRIGGERED;
                    this.callHandlers();
                } else {
                    log.debug(`ReadySignal ${this.name} triggerd, unmet dependencies: `,
                              Array.from(this.dependencies).map(d => d.name));
                }
                break;
            case Status.TRIGGERED:
                // calling trigger() multiple times has no effect
                break;
        }
    }

    /**
     * If you want to use a ReadySignal for processes outside of page loading,
     * then you'll either need to re-create the dependency tree, or you might
     * be able to get by with just resetting the tree with this method.
     */
    public reset() {
        // If we're not ready, resetting makes no sense. One way we could get
        // into this situation is a circular dependency, so returning without
        // recursing should avoid a infinite recursion.
        if(this.status == Status.FRESH) {
            log.debug(`ReadySignal.reset() attempt to reset a fresh ReadySignal "${this.name}"`);
            return;
        }

        this.status = Status.FRESH
        for(const other of this.dependencies) {
            other.reset();
        }
    }

    /**
     * Recursively assembles a list of items we are waiting for.
     */
    public awaiting(list?: ReadySignal[]) {
        if(list == null) list = [];
        if(this.status != Status.TRIGGERED) {
            list.push(this);
        }
        for(const dep of this.dependencies) {
            dep.awaiting(list);
        }
        return list;
    }

    /**
     * Unconditionally calls all handlers.
     */
    private callHandlers() {
        for(const handler of this.handlers) {
            try {
                handler();
            } catch(error) {
                log.error(error);
            }
        }
    }

    /**
     * Checks if every dependency has the TRIGGERED status.
     */
    private allDependenciesHaveTriggered() {
        // TODO: could probably count dependencies added and dependencies
        // triggered which would make this much lighter weight.
        for(const d of this.dependencies) {
            if(d.status != Status.TRIGGERED) {
                return false;
            }
        }
        return true;
    }

    /**
     * Called by each dependency when it becomes ready. We add this method to
     * each dependency in the `depends` method so that when it triggers we will
     * then re-check if all our dependencies are met.
     */
    private resolveDependency = () => {
        if(this.status == Status.TRIGGERED) {
            log.debug(`ReadySignal.resolveDependency() "${this.name}" has already been triggered`);
        } else if(
            this.status == Status.READY && this.allDependenciesHaveTriggered())
        {
            this.status = Status.TRIGGERED;
            log.debug(`ReadySignal ${this.name} dependency resolved, READY -> TRIGGERED, calling handlers`);
            this.callHandlers();
        }
    }
}

// Note, a useful debug function for the console:
/*
report = rs => {
    if(rs.status != 2) { console.log(rs.name) }
    for(const ch of rs.dependencies) {
        report(ch);
    }
}
*/

